import Container from '@mui/material/Container';
import {
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
} from '@mui/material';
import CustomizedTable from 'modules/requests/components/Table';
import React, { useEffect, useRef, useState } from 'react';
import { RequestApi } from 'lib/api/requests';
import { CompanyApi } from 'lib/api/company';
import IosShareIcon from '@mui/icons-material/IosShare';
import {
  BaseCardBox,
  BaseTextField,
  BaseTextFieldCPF,
  FakeButton,
  FieldRow,
  Header,
} from './style';
import { FileApi } from 'lib/api/file';
import { useSearchParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  ICompany,
  IRequestResponseDTO,
  ITableBody,
} from 'modules/requests/types';
import { Visibility } from '@mui/icons-material';
import dayjs from 'dayjs';
import Tooltip from '@mui/material/Tooltip';
import {
  MessagingApi,
  whatsappMessageForced,
} from '../../../../lib/api/messaging';
import { useUser } from '../../../auth/context/auth.consumer';
import { RoleType } from '../../../auth/common/role';
import { BaseExportExcelContainer } from '../../../sectors/pages/list/style';
import { cpfMask } from '../../../../lib/helpers/masks';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useWhatsappPolling } from '../../../../lib/whatsapp/useWhatsappPolling';
import { WhatsappSendButton } from '../../../../lib/whatsapp/WhatsappSendButton';
import { WhatsappStatusLegend } from '../../../../lib/whatsapp/WhatsappStatusLegend';
import { brand } from 'themes/brand';

const tableHead = [
  'Empresa',
  'Nome do candidato',
  'Solicitação',
  'Status',
  'Mensagens',
  '',
];

const ConsultantRequestListPage = ({
  onViewDetails,
}: {
  onViewDetails: (id: string) => void;
}) => {
  const user = useUser();
  const [companies, setCompanies] = useState<ICompany[]>();
  const [tableBody, setTableBody] = useState<ITableBody[]>([]);
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState<number | undefined>(undefined);
  const [excelLoading, setExcelLoading] = useState(false);
  const lastEntitiesRef = useRef<IRequestResponseDTO['entities']>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();

  const {
    isMountedRef,
    sendingWhatsapp,
    whatsappStatuses,
    setWhatsappStatuses,
    pollWhatsappStatus,
    startSending,
    stopSending,
  } = useWhatsappPolling();

  const candidateName = searchParams.get('candidateName') || '';
  const consultantName = searchParams.get('consultantName') || '';
  const companyName = searchParams.get('companyName') || '';
  const status = searchParams.get('status') || '';
  const candidateCpf = searchParams.get('candidateCpf') || '';
  const startDate = searchParams.get('startDate') || '';
  const endDate = searchParams.get('endDate') || '';

  const hasCompleteDateRange = startDate && endDate;
  const dateParams = hasCompleteDateRange
    ? `&startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(
        endDate,
      )}`
    : '';

  const queryString = `assessment=false&candidateName=${encodeURIComponent(
    candidateName,
  )}&companyName=${
    encodeURIComponent(companyName) === 'todas'
      ? ''
      : encodeURIComponent(companyName)
  }&consultantName=${encodeURIComponent(
    consultantName,
  )}&status=${encodeURIComponent(
    status || '',
  )}&candidateCpf=${encodeURIComponent(candidateCpf)}${dateParams}`;

  const handleWhatsappMsgSender = async (
    messageBodyProps: whatsappMessageForced,
    typeMessage: string,
  ) => {
    if (user?.role === RoleType.ANALISTA || user?.role === RoleType.CONSULTOR) {
      return enqueueSnackbar(
        `Perfil ANALISTA & CONSULTOR não tem permissão para enviar mensagem WhatsApp ao candidato!`,
        { variant: 'error', autoHideDuration: 10000 },
      );
    }

    const requestId = messageBodyProps.analystRequestId;
    const sendingKey = `${requestId}:${typeMessage}`;
    startSending(sendingKey);

    try {
      const sent = await MessagingApi.sendWhatsAppMessageForced(
        messageBodyProps,
        typeMessage,
      );

      enqueueSnackbar(
        `Mensagem enviada. Aguardando confirmação da operadora...`,
        {
          variant: 'info',
          autoHideDuration: 5000,
        },
      );

      await pollWhatsappStatus(
        requestId,
        typeMessage,
        messageBodyProps.candidateWhatsapp,
        sent?.messageSid,
      );
    } catch (error: any) {
      if (error?.response?.data?.statusCode === 403) {
        enqueueSnackbar(`Sem permissão para enviar mensagem WhatsApp.`, {
          variant: 'error',
          autoHideDuration: 10000,
        });
      } else {
        enqueueSnackbar(
          error?.response?.data?.message ||
            `Erro ao enviar mensagem WhatsApp. Tente novamente ou acione o suporte.`,
          { variant: 'error', autoHideDuration: 10000 },
        );
      }
    } finally {
      if (isMountedRef.current) {
        stopSending(sendingKey);
      }
    }
  };

  const searchParamsObject = Object.fromEntries(searchParams.entries());

  // TODO: extrair updateSearchParams e updateOnlyCandidateCpfParam para src/lib/hooks/useSearchParamsSync.ts (idêntico em assessment-list)
  const updateSearchParams = (param: string, value: string) => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (param === 'candidateCpf') {
      newSearchParams.set(param, cpfMask(value));
    } else {
      newSearchParams.set(param, value);
    }
    setSearchParams(newSearchParams);
  };

  const updateOnlyCandidateCpfParam = () => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (
      searchParamsObject['candidateCpf'] &&
      searchParamsObject['candidateCpf'].length > 0
    ) {
      newSearchParams.set(
        'candidateCpf',
        cpfMask(searchParamsObject['candidateCpf']),
      );
      setSearchParams(newSearchParams);
    }
  };

  const fetchData = async (page: number) => {
    try {
      const [requestList, companiesList] = await Promise.all([
        RequestApi.searchRequestsWithoutAssessment(queryString, page),
        CompanyApi.getCompanies(),
      ]);

      setPageCount(requestList.meta.pageCount);
      setCompanies(companiesList);

      const entities = requestList.entities as IRequestResponseDTO['entities'];
      lastEntitiesRef.current = entities;
      mountTableBody(entities, {}, {});

      const ids = entities.map((item) => item.id);
      const batchResult = await MessagingApi.getWhatsappStatusBatch(ids);

      const newStatuses: Record<
        string,
        { status: string | null; updatedAt: string | null }
      > = {};
      Object.entries(batchResult).forEach(([key, val]) => {
        newStatuses[key] = {
          status: val?.messageStatus ?? null,
          updatedAt: val?.updatedAt ?? null,
        };
      });

      if (isMountedRef.current) {
        setWhatsappStatuses(newStatuses);
      }
    } catch (error: any) {
      if (error.code === 'ERR_NETWORK') {
        return enqueueSnackbar(
          'Servidor instável, aguarde alguns segundos e recarregue a página por favor',
          { variant: 'error' },
        );
      }
      console.error(error);
    }
  };

  // TODO: extrair para src/lib/hooks/useExcelExport.ts (idêntico em assessment-list, diferindo apenas na chamada FileApi)
  const getExportRequestsLink = async () => {
    setExcelLoading(true);
    try {
      const url = await FileApi.exportRequests(queryString);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', url);
      document.body.appendChild(link);
      link.click();
    } catch (e) {
      setExcelLoading(false);
      console.error(e);
      enqueueSnackbar('Erro ao exportar as solicitações', { variant: 'error' });
    } finally {
      setExcelLoading(false);
    }
  };

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (lastEntitiesRef.current.length > 0) {
      mountTableBody(
        lastEntitiesRef.current,
        whatsappStatuses,
        sendingWhatsapp,
      );
    }
  }, [whatsappStatuses, sendingWhatsapp]);

  useEffect(() => {
    updateOnlyCandidateCpfParam();
    fetchData(page);
  }, [queryString, page]);

  const fakeButtonBgColor = (variant: string) => {
    switch (variant) {
      case 'em andamento' || 'apto com restrição':
        return brand.status.warning.bg;
      case 'cancelado' || 'inapto':
        return brand.status.error.bg;
      case 'correção':
        return brand.status.attention.bg;
      case 'congelado' || 'excede':
        return brand.status.neutral.bg;
      case 'finalizado' || 'apto':
        return brand.status.success.bg;
      case 'reanálise':
        return brand.status.info.bg;
    }
  };

  const mountTableBody = (
    requestList: IRequestResponseDTO['entities'],
    statuses: Record<
      string,
      { status: string | null; updatedAt: string | null }
    > = {},
    sending: Record<string, boolean> = {},
  ) => {
    const body: ITableBody[] = [];
    requestList?.forEach((item) => {
      const requestId = item.id;
      const itemStatus = item.status?.toLowerCase();
      const isDisabled =
        itemStatus !== 'em andamento' && itemStatus !== 'reanálise';
      const avisoStatus = statuses[`${requestId}:aviso`]?.status ?? null;
      const avisoUpdatedAt = statuses[`${requestId}:aviso`]?.updatedAt ?? null;
      const cobrancaStatus = statuses[`${requestId}:cobrar`]?.status ?? null;
      const cobrancaUpdatedAt =
        statuses[`${requestId}:cobrar`]?.updatedAt ?? null;
      const isSendingAviso = !!sending[`${requestId}:aviso`];
      const isSendingCobranca = !!sending[`${requestId}:cobrar`];

      body.push({
        company: item.company.name,
        candidateName: item.candidate.name,
        requestDate: dayjs(item.requestDate).format('DD/MM/YYYY'),
        status: (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <FakeButton
              variant={fakeButtonBgColor(
                item && item.status && item.status.toLowerCase(),
              )}
            >
              {item.status}
            </FakeButton>
          </div>
        ),
        result: (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <FakeButton
              variant={fakeButtonBgColor(
                item.requestManagement.result
                  ? item.requestManagement.result.toLowerCase()
                  : '',
              )}
            >
              <p>
                {item.requestManagement.result
                  ? item.requestManagement.result.toLowerCase()
                  : '...'}
              </p>
            </FakeButton>
          </div>
        ),
        actions: (
          <div onClick={() => onViewDetails(item.id)}>
            <Visibility />
          </div>
        ),
        whatsapp: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexFlow: 'row',
              gap: '8px',
            }}
          >
            <WhatsappSendButton
              label="Aviso"
              status={avisoStatus}
              updatedAt={avisoUpdatedAt}
              isSending={isSendingAviso}
              isDisabled={isDisabled}
              onClick={() =>
                handleWhatsappMsgSender(
                  {
                    analystName: item.analyst.name,
                    companyName: item.company.name,
                    candidateName: item.candidate.name,
                    candidateEmail: item.candidate.email,
                    candidateWhatsapp: item.candidate.whatsapp,
                    analystRequestId: requestId,
                    dateLinkSent: null,
                  },
                  'aviso',
                )
              }
            />
            <WhatsappSendButton
              label="Cobrança"
              status={cobrancaStatus}
              updatedAt={cobrancaUpdatedAt}
              isSending={isSendingCobranca}
              isDisabled={isDisabled}
              onClick={() =>
                handleWhatsappMsgSender(
                  {
                    analystName: item.analyst.name,
                    companyName: item.company.name,
                    candidateName: item.candidate.name,
                    candidateEmail: item.candidate.email,
                    candidateWhatsapp: item.candidate.whatsapp,
                    analystRequestId: requestId,
                    dateLinkSent: item.requestDate,
                  },
                  'cobrar',
                )
              }
            />
          </div>
        ),
      });
    });
    setTableBody(body);
  };

  return (
    <Container>
      <BaseCardBox>
        <Header>
          <h3>Solicitações</h3>
        </Header>
        <Stack
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '2rem',
          }}
        >
          <BaseTextField
            onChange={(event) => {
              page !== 1 && setPage(1);
              updateSearchParams('candidateName', event.target.value);
            }}
            label="Nome do candidato"
            variant="outlined"
            value={searchParamsObject.candidateName}
          />

          <FormControl>
            <InputLabel id="demo-simple-select-label">Empresa</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              label="Company"
              onChange={(event) => {
                page !== 1 && setPage(1);
                updateSearchParams(
                  'companyName',
                  event.target.value === 'todas' ? '' : event.target.value,
                );
              }}
              value={searchParamsObject.companyName || 'todas'}
            >
              <MenuItem value="todas">Todas</MenuItem>
              {companies?.map((item) => (
                <MenuItem value={item.name} key={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl>
            <InputLabel id="demo-simple-select-label">Status</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              label="Status"
              onChange={(event) => {
                page !== 1 && setPage(1);
                updateSearchParams(
                  'status',
                  event.target.value === 'todos' ? '' : event.target.value,
                );
              }}
              value={searchParamsObject.status || 'todos'}
            >
              <MenuItem value="todos">Todos</MenuItem>
              <MenuItem value="em andamento">Em andamento</MenuItem>
              <MenuItem value="finalizado">Finalizado</MenuItem>
              <MenuItem value="cancelado">Cancelado</MenuItem>
              <MenuItem value="congelado">Congelado</MenuItem>
              <MenuItem value="correção">Correção</MenuItem>
              <MenuItem value="reanálise">Reanálise</MenuItem>
            </Select>
          </FormControl>

          <BaseTextFieldCPF
            fullWidth
            type="text"
            onChange={(event) => {
              page !== 1 && setPage(1);
              updateSearchParams('candidateCpf', event.target.value);
            }}
            label="CPF do candidato"
            variant="outlined"
            value={cpfMask(searchParamsObject.candidateCpf)}
          />

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              format="DD/MM/YYYY"
              label="Data de início"
              value={
                searchParamsObject?.startDate?.length > 0
                  ? dayjs(startDate)
                  : undefined
              }
              onChange={(newValue) => {
                let startDate = '';
                if (!newValue || !dayjs(newValue).isValid()) {
                  startDate = '';
                } else {
                  startDate = dayjs(newValue).format('YYYY-MM-DD');
                }
                page !== 1 && setPage(1);
                updateSearchParams('startDate', startDate);
              }}
              slotProps={{
                textField: {
                  error: !!(startDate && !endDate),
                  helperText:
                    startDate && !endDate
                      ? 'Preencha também a data de término para filtrar'
                      : '',
                },
              }}
            />
          </LocalizationProvider>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              format="DD/MM/YYYY"
              label="Data de término"
              value={
                searchParamsObject?.endDate?.length > 0
                  ? dayjs(endDate)
                  : undefined
              }
              onChange={(newValue) => {
                let endDate = '';
                if (!newValue || !dayjs(newValue).isValid()) {
                  endDate = '';
                } else {
                  endDate = dayjs(newValue).format('YYYY-MM-DD');
                }
                page !== 1 && setPage(1);
                updateSearchParams('endDate', endDate);
              }}
              slotProps={{
                textField: {
                  error: !!(endDate && !startDate),
                  helperText:
                    endDate && !startDate
                      ? 'Preencha também a data de início para filtrar'
                      : '',
                },
              }}
            />
          </LocalizationProvider>
        </Stack>
      </BaseCardBox>

      <BaseExportExcelContainer>
        <WhatsappStatusLegend />
        <div>
          <Tooltip title="Exportar em excel todos os registros de solicitações">
            <Button
              variant="contained"
              startIcon={
                excelLoading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : (
                  <IosShareIcon />
                )
              }
              onClick={() => getExportRequestsLink()}
              disabled={excelLoading}
            >
              Exportar em excel
            </Button>
          </Tooltip>
        </div>
      </BaseExportExcelContainer>

      <CustomizedTable tableHead={tableHead} tableBody={tableBody} />

      <Stack spacing={2} alignItems={'center'}>
        <Pagination
          count={pageCount}
          page={page}
          onChange={(e, value) => setPage(value)}
          defaultPage={page}
        />
      </Stack>
    </Container>
  );
};

export { ConsultantRequestListPage };
export default ConsultantRequestListPage;
